import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAddress } from "@thirdweb-dev/react";
import { ethers } from "ethers";
import "../styles/staking-box1.css";
import { useStakingContract } from "../hooks/useStakingContract";
import { SUPPORTED_CHAINS } from "../contracts/addresses";

const StakingBox1 = () => {
  const { t } = useTranslation();
  const address = useAddress();
  const [isStaking, setIsStaking] = useState(false);
  const [amount, setAmount] = useState("");
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [transactionHash, setTransactionHash] = useState("");
  const [modalStep, setModalStep] = useState("input"); // "input", "processing", "success"
  
  const { stakedBalance, stakeableBalance, stake, refreshData } = useStakingContract();

  // Bloquear scroll cuando el modal está abierto
  useEffect(() => {
    if (showStakeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showStakeModal]);

  // Formatear balance con manejo de errores
  const formatBalance = (balance) => {
    if (!balance) return "0";
    try {
      return parseFloat(ethers.utils.formatEther(balance)).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      console.error("Error al formatear balance:", error);
      return "0";
    }
  };

  // Obtener valor numérico del balance
  const getRawBalance = (balance) => {
    if (!balance) return 0;
    try {
      return parseFloat(ethers.utils.formatEther(balance));
    } catch (error) {
      return 0;
    }
  };
  
  // Estados para el modal de notificación (estilo StakingBox4)
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success"); // success, error

  // Mostrar mensaje en modal en lugar de alert
  const showMessage = (message, type = "success") => {
    setModalMessage(message);
    setModalType(type);
    setShowNotificationModal(true);
  };
  
  // Cerrar modal de notificación
  const closeNotificationModal = () => {
    setShowNotificationModal(false);
  };

  // Manejar stake
  const handleStake = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      showMessage(t('stakingBox1.enterValidAmount'), "error");
      return;
    }

    try {
      setIsStaking(true);
      setModalStep("processing");
      
      const amountInWei = ethers.utils.parseEther(amount.toString());
      const transaction = await stake(amountInWei);
      
      if (transaction.receipt) {
        await transaction.receipt.wait();
      } else if (transaction.wait) {
        await transaction.wait(1);
      }
      
      setTransactionHash(transaction.receipt?.transactionHash || transaction.hash);
      setModalStep("success");
      await refreshData();
    } catch (error) {
      console.error("Error al stakear tokens:", error);
      
      // IMPORTANTE: Cerrar el modal de procesamiento primero
      setShowStakeModal(false);
      
      // Pequeña espera para asegurar que el modal se cierre completamente
      setTimeout(() => {
        // Simplificación de mensajes de error para mostrar solo mensajes amigables
        let errorMessage = t('stakingBox1.errorStaking');
        
        // Detectar posibles casos de rechazo de transacción
        if (error.code === 4001 || 
            (error.message && (
              error.message.includes("user rejected") || 
              error.message.includes("User denied") || 
              error.message.includes("user denied") || 
              error.message.includes("rejected") ||
              error.message.includes("cancelled") ||
              error.message.includes("canceled")
            ))) {
          errorMessage = t('stakingBox1.userRejected');
        } else if (error.message && error.message.includes("insufficient funds")) {
          errorMessage = t('stakingBox1.insufficientFunds');
        } else if (error.message && error.message.includes("execution reverted")) {
          errorMessage = t('stakingBox1.contractRejection');
        }
        
        // Mostrar el mensaje de error en el modal de notificación
        showMessage(errorMessage, "error");
      }, 300); // Pequeño retraso para asegurar que el primer modal se cierre
      
      // Reiniciar el estado del modal para futuras interacciones
      setModalStep("input");
    } finally {
      setIsStaking(false);
    }
  };

  // Administrar modal
  const openStakeModal = () => {
    if (!address) {
      showMessage(t('stakingBox1.connectWallet'), "error");
      return;
    }
    setModalStep("input");
    setAmount("");
    setShowStakeModal(true);
  };

  const closeModal = () => {
    setShowStakeModal(false);
    setTimeout(() => {
      setAmount("");
      setModalStep("input");
    }, 200);
    refreshData();
  };

  // Función simplificada para abrir ESPECÍFICAMENTE en BscScan (testnet o mainnet)
  const viewTransaction = () => {
    console.log("Abriendo transacción en BscScan:", transactionHash);
    
    if (!transactionHash) {
      console.error("No hay hash de transacción disponible");
      closeModal();
      return;
    }
    
    // Determinar si estamos en testnet o mainnet de BSC
    let bscScanUrl = "https://bscscan.com"; // Mainnet por defecto
    
    try {
      // Obtener chainId para determinar la red
      const chainId = window.ethereum?.chainId;
      console.log("ChainID detectado:", chainId);
      
      // Si estamos en BSC Testnet (chainId 97)
      if (chainId && parseInt(chainId, 16) === 97) {
        bscScanUrl = "https://testnet.bscscan.com";
      }
      // Si estamos en BSC Mainnet (chainId 56)
      else if (chainId && parseInt(chainId, 16) === 56) {
        bscScanUrl = "https://bscscan.com";
      }
    } catch (error) {
      console.error("Error al detectar la red:", error);
    }
    
    // Construir URL completa con el hash
    const txUrl = `${bscScanUrl}/tx/${transactionHash}`;
    console.log("URL de BscScan:", txUrl);
    
    // Abrir en nueva pestaña
    window.open(txUrl, '_blank');
    
    // Cerrar el modal
    setTimeout(() => closeModal(), 300);
  };

  return (
    <div className="staking-box">
      <div className="box-content">
        <div>
          <h4>{t('stakingBox1.stakedBalance')}</h4>
          <p className="value">
            {address ? formatBalance(stakedBalance) : "0"}
            <span className="unit green-text">$eDOP</span>
          </p>
        </div>
        <div>
          <h4>{t('stakingBox1.stakeable')}</h4>
          <p className="value">
            {address ? formatBalance(stakeableBalance) : "0"}
            <span className="unit green-text">$eDOP</span>
          </p>
        </div>
      </div>
      
      <button 
        id="stake-button"
        onClick={openStakeModal}
        disabled={!address || !stakeableBalance || ethers.BigNumber.from(stakeableBalance || "0").lte(0)}
      >
        {t('stakingBox1.stakeNow')}
      </button>
      
      {/* Modal de notificación estilo StakingBox4 */}
      {showNotificationModal && (
        <div className="modal-overlay" onClick={closeNotificationModal}>
          <div className={`modal-container ${modalType}`} onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeNotificationModal}>&times;</span>
            <div className="modal-content">
              <h3>{modalType === "success" ? t('stakingBox1.success') : t('stakingBox1.info')}</h3>
              <p className={modalType === "error" ? "smaller-text" : ""}>{modalMessage}</p>
              <button className="modal-button" onClick={closeNotificationModal}>
                {t('stakingBox1.accept')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal con contenido condicional */}
      {showStakeModal && (
        <div className="modal-overlay" onClick={modalStep === "input" ? closeModal : null}>
          <div className={`stake-modal-compact ${modalStep === "success" ? "success-mode" : ""}`} 
               onClick={e => e.stopPropagation()}>
            {modalStep === "processing" ? (
              <div className="processing-container">
                <div className="spinner"></div>
                <h3 className="fredoka-font">{t('stakingBox1.processingTransaction')}</h3>
                <p>{t('stakingBox1.waitForStaking')}</p>
              </div>
            ) : modalStep === "success" ? (
              <>
                <div className="success-container">
                  <img src="/icons/success-checkmark.svg" alt="Éxito" className="success-icon" />
                  <h3 className="fredoka-font">{t('stakingBox1.transactionSuccess')}</h3>
                  <p className="baloo-font">{t('stakingBox1.successStaked', { amount: parseFloat(amount).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) })}</p>
                </div>
                <div className="transaction-buttons single-button">
                  <button className="modal-btn exit-btn" onClick={closeModal}>{t('stakingBox1.exit')}</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="fredoka-font">{t('stakingBox1.stakeTokens')}</h3>
                <div className="amount-display">
                  <span>{parseFloat(amount || 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                  <span className="green-text">$eDOP</span>
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    min="0"
                    max={getRawBalance(stakeableBalance)}
                    step="1"
                    value={parseFloat(amount) || 0}
                    onChange={(e) => setAmount(e.target.value)}
                    className="slider-input"
                  />
                  <div className="slider-labels">
                    <span>0</span>
                    <span>{t('stakingBox1.available', { amount: formatBalance(stakeableBalance) })}</span>
                  </div>
                </div>
                <div className="modal-buttons-reversed">
                  <button 
                    className="modal-btn confirm baloo-font"
                    onClick={handleStake}
                    disabled={isStaking || !amount || parseFloat(amount) <= 0}
                  >
                    {isStaking ? "..." : t('stakingBox1.confirm')}
                  </button>
                  <button className="modal-btn cancel baloo-font" onClick={closeModal}>
                    {t('stakingBox1.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StakingBox1;
